/**
 * /flows/templates — gallery of pre-built flow templates. Cycle 19.
 *
 * Click "Use template" → POST /flows/from-template → redirect to the
 * flow builder so the user can edit + publish their copy.
 */
import { useEffect, useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface TemplateCard {
  id: string;
  slug: string;
  name: string;
  category: string;
  description: string;
  screenshotUrl: string | null;
}

export const Route = createFileRoute("/_app/flows/templates")({
  component: TemplatesPage,
});

function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ templates: TemplateCard[] }>("/flow-templates")
      .then((r) => setTemplates(r.data.templates))
      .catch(() => setError("Failed to load templates."))
      .finally(() => setLoading(false));
  }, []);

  async function useTemplate(slug: string) {
    setBusyId(slug);
    setError(null);
    try {
      const r = await api.post<{ flow: { id: string } }>(
        "/flows/from-template",
        { templateSlug: slug },
      );
      void navigate({
        to: "/flows/$flowId/edit",
        params: { flowId: r.data.flow.id },
      });
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string; message?: string } } }).response?.data;
      if (data?.code === "PLAN_LIMIT_EXCEEDED") {
        setError(`${data.message} Upgrade in Settings → Billing.`);
      } else {
        setError(data?.message ?? "Failed to clone template");
      }
      setBusyId(null);
    }
  }

  // Group by category for the gallery.
  const byCategory = templates.reduce<Record<string, TemplateCard[]>>((acc, t) => {
    (acc[t.category] ||= []).push(t);
    return acc;
  }, {});

  return (
    <main style={{ maxWidth: 920, margin: "0 auto", padding: "16px 24px" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 28, margin: 0 }}>Flow templates</h1>
        <Link to="/flows" className="brut-button is-secondary">
          ← Flows
        </Link>
      </header>
      <p style={{ color: "var(--muted)", marginTop: 4, marginBottom: 24 }}>
        Pick a starting point. Each template clones into your workspace as a
        draft you can edit and publish.
      </p>

      {error ? <div className="brut-error" style={{ marginBottom: 12 }}>{error}</div> : null}
      {loading ? <p>Loading…</p> : null}

      {Object.entries(byCategory).map(([category, items]) => (
        <section key={category} style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 14, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {category}
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 12,
            }}
          >
            {items.map((t) => (
              <article
                key={t.id}
                className="brut-card"
                style={{ padding: 14, display: "grid", gap: 10 }}
              >
                {t.screenshotUrl ? (
                  <img
                    src={t.screenshotUrl}
                    alt=""
                    style={{
                      width: "100%",
                      height: 140,
                      objectFit: "cover",
                      border: "1px solid var(--border)",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: 100,
                      background: "var(--bg-soft)",
                      border: "1px solid var(--border)",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 32,
                    }}
                  >
                    🤖
                  </div>
                )}
                <div style={{ fontWeight: 800, fontSize: 16 }}>{t.name}</div>
                <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.4 }}>
                  {t.description}
                </div>
                <button
                  onClick={() => void useTemplate(t.slug)}
                  disabled={busyId === t.slug}
                  className="brut-button"
                  style={{ width: "100%" }}
                >
                  {busyId === t.slug ? "Cloning…" : "Use template"}
                </button>
              </article>
            ))}
          </div>
        </section>
      ))}
    </main>
  );
}
